import { Button, Chip, Icon, Paper, Typography } from '@shared/ui';
import { useEffect, useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { DebateVotePromptEntity, TimelineWidgetProps } from '../types';

export interface DebateVotePromptWidgetProps extends TimelineWidgetProps<DebateVotePromptEntity> {
  debugMode?: boolean;
}

registerWidgetRenderer(
  'debate_vote_prompt',
  ({ entity, debugMode }) => (
    <DebateVotePromptWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function DebateVotePromptWidget({ entity, debugMode = false }: DebateVotePromptWidgetProps) {
  const {
    status,
    vote_prompt_id,
    question_id,
    round_number,
    prompt_text,
    options,
    selected_option_id,
  } = entity.props;
  const [localSelected, setLocalSelected] = useState<string | undefined>(selected_option_id);

  // Sync with entity props if they change externally
  useEffect(() => {
    if (selected_option_id) {
      setLocalSelected(selected_option_id);
    }
  }, [selected_option_id]);

  const handleVote = (optionId: string) => {
    setLocalSelected(optionId);
    // Store vote in localStorage for persistence across page refreshes
    if (vote_prompt_id) {
      localStorage.setItem(`debate_vote_${vote_prompt_id}`, optionId);
    }
  };

  // Load vote from localStorage on mount
  useEffect(() => {
    if (vote_prompt_id && !localSelected) {
      const stored = localStorage.getItem(`debate_vote_${vote_prompt_id}`);
      if (stored) {
        setLocalSelected(stored);
      }
    }
  }, [vote_prompt_id, localSelected]);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const selectedOptionId = localSelected;

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-purple-50 to-pink-50">
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'completed' ? 'how_to_vote' : 'ballot'}
        </Icon>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <Typography variant="subtitle1" fontWeight="bold">
              🗳️ Vote
            </Typography>
            {round_number !== undefined ? (
              <Chip label={`Round ${round_number}`} size="small" variant="outlined" />
            ) : null}
            {question_id ? (
              <Chip label={`Q: ${question_id}`} size="small" color="primary" variant="outlined" />
            ) : null}
          </div>

          {status === 'active' || status === 'completed' ? (
            <div className="space-y-3">
              {prompt_text ? (
                <Typography variant="body1" fontWeight="medium" className="mb-3">
                  {prompt_text}
                </Typography>
              ) : null}

              {options && options.length > 0 ? (
                <div className="space-y-2">
                  {options.map((option) => {
                    const isSelected = selectedOptionId === option.option_id;
                    return (
                      <Button
                        key={option.option_id}
                        variant={isSelected ? 'contained' : 'outlined'}
                        color={isSelected ? 'primary' : 'inherit'}
                        fullWidth
                        onClick={() => handleVote(option.option_id)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          padding: '12px 16px',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          backgroundColor: isSelected ? 'primary.light' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                          },
                        }}
                      >
                        <div className="flex flex-col items-start gap-1 w-full">
                          <Typography
                            variant="subtitle2"
                            fontWeight={isSelected ? 'bold' : 'medium'}
                          >
                            {option.label}
                          </Typography>
                          {option.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="text-left"
                            >
                              {option.description}
                            </Typography>
                          ) : null}
                        </div>
                        {isSelected ? <Icon sx={{ ml: 'auto' }}>check_circle</Icon> : null}
                      </Button>
                    );
                  })}
                </div>
              ) : null}

              {selectedOptionId && (
                <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                  <Typography variant="caption" color="success.main">
                    ✓ You voted for:{' '}
                    {options?.find((opt) => opt.option_id === selectedOptionId)?.label ||
                      selectedOptionId}
                  </Typography>
                </div>
              )}

              {status === 'completed' && entity.props.error ? (
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    Error: {entity.props.error}
                  </Typography>
                </div>
              ) : null}

              {debugMode ? (
                <details className="bg-gray-100 rounded p-2">
                  <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                  <pre className="text-xs mt-2 overflow-auto max-h-48">
                    {JSON.stringify(entity.props, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="mt-3">
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <Typography variant="body2" color="error">
                  {entity.props.error || 'An error occurred while processing vote prompt'}
                </Typography>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Paper>
  );
}
